/**
 * Suscripción a Server-Sent Events del backend.
 *
 * `useInboxStream(clientSlug)` abre EventSource a
 * `/api/moderation/stream/:clientSlug` y al recibir eventos invalida los
 * queries del inbox + thread. Los eventos publicados por el backend:
 *
 *  - `message.in`        → llegó un DM o comentario nuevo
 *  - `message.out`       → bot respondió
 *  - `message.escalated` → escalación automática
 *  - `conversation.taken` / `conversation.released` → action humana
 *
 * `useAlertStream` queda como wrapper de `useAlerts` para que el header
 * pueda mostrar un contador realtime sin cambios de API.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAlerts } from "./use-alerts";
import type { AlertEvent, ClientSlug } from "@/types";

type InboxEvent =
  | "ready"
  | "message.in"
  | "message.out"
  | "message.escalated"
  | "conversation.taken"
  | "conversation.released"
  | "conversation.escalated"
  | "conversation.closed";

export function useInboxStream(clientSlug: ClientSlug | undefined): {
  connected: boolean;
} {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!clientSlug) return;
    const url = `/api/moderation/stream/${clientSlug}`;
    const es = new EventSource(url, { withCredentials: true });

    const invalidate = () => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.invalidateQueries({ queryKey: ["messages"] });
      qc.invalidateQueries({ queryKey: ["conversation"] });
    };

    const handlers: Record<InboxEvent, EventListener> = {
      ready: () => setConnected(true),
      "message.in": invalidate,
      "message.out": invalidate,
      "message.escalated": () => {
        invalidate();
        qc.invalidateQueries({ queryKey: ["alerts"] });
      },
      "conversation.taken": invalidate,
      "conversation.released": invalidate,
      "conversation.escalated": () => {
        // Cuando un humano escala una conv, otros tabs ven el cambio inmediato
        // + las alertas se refrescan por si dispararon nuevas notifs.
        invalidate();
        qc.invalidateQueries({ queryKey: ["alerts"] });
      },
      "conversation.closed": invalidate,
    };

    for (const [event, handler] of Object.entries(handlers)) {
      es.addEventListener(event, handler);
    }

    es.onerror = () => setConnected(false);

    return () => {
      for (const event of Object.keys(handlers)) {
        es.removeEventListener(event, handlers[event as InboxEvent]);
      }
      es.close();
      setConnected(false);
    };
  }, [clientSlug, qc]);

  return { connected };
}

/**
 * Adapter de `useAlerts` que devuelve la shape vieja del header
 * (`{ alerts, connected, acknowledge }`). Mientras el header no migre,
 * lo mantenemos compatible.
 */
export function useAlertStream(): {
  alerts: AlertEvent[];
  connected: boolean;
  acknowledge: (id: string) => void;
} {
  const { data, isLoading } = useAlerts({ unresolvedOnly: true, limit: 20 });
  const alerts: AlertEvent[] = (data ?? []).map((a) => ({
    id: String(a.id),
    level: a.severity,
    client: a.clientSlug,
    title: a.kind,
    body: a.message,
    createdAt: a.createdAt,
    acknowledged: a.acknowledged,
  }));
  return {
    alerts,
    connected: !isLoading,
    acknowledge: () => {
      // El ack real vive en useAckAlert. Esta función queda como no-op para
      // no romper consumidores legacy del header.
    },
  };
}
